/**
 * SEO helper — sets document.title and <meta> tags imperatively.
 * Avoids the need for react-helmet-async as a dependency.
 */
import { useEffect } from "react";
import { devWarn } from "@/lib/devLog";

const upsertMetaName = (name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertMetaProperty = (property, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, attrs = {}) => {
  if (!href) return;
  const selector =
    rel === "alternate" && attrs.hreflang
      ? `link[rel="alternate"][hreflang="${attrs.hreflang}"]`
      : `link[rel="${rel}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

export function useSEO({ title, description, image, canonical, alternates = [], jsonLd, robots } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMetaName("description", description);
      upsertMetaProperty("og:description", description);
      upsertMetaName("twitter:description", description);
    }
    if (title) {
      upsertMetaProperty("og:title", title);
      upsertMetaName("twitter:title", title);
    }
    if (image) {
      upsertMetaProperty("og:image", image);
      upsertMetaName("twitter:image", image);
      upsertMetaName("twitter:card", "summary_large_image");
    }
    if (canonical) {
      upsertLink("canonical", canonical);
      upsertMetaProperty("og:url", canonical);
    } else {
      // No canonical for this route → fall back to a self-canonical on the
      // production domain so noindex pages don't inherit a stale canonical
      // pointing to the previously visited page.
      try {
        const fallback = `https://doing-good.org${typeof window !== "undefined" ? window.location.pathname || "/" : "/"}`;
        upsertLink("canonical", fallback);
        upsertMetaProperty("og:url", fallback);
      } catch (err) {
        devWarn("useSEO: canonical fallback failed", err);
      }
    }
    alternates.forEach(({ hreflang, href }) => {
      upsertLink("alternate", href, { hreflang });
    });
    if (jsonLd) upsertJsonLd("page-jsonld", jsonLd);
    // Always set robots — defaults to "index, follow" so previous noindex
    // pages don't leak across navigations.
    upsertMetaName("robots", robots || "index, follow");
    return () => {
      removeJsonLd("page-jsonld");
      // Reset robots so a stale "noindex" never sticks for the next route.
      upsertMetaName("robots", "index, follow");
    };
  }, [title, description, image, canonical, robots, JSON.stringify(alternates), JSON.stringify(jsonLd)]);
}
